(() => {
  const FRACTION_GLYPHS = { 0: "", 1: "¼", 2: "½", 3: "¾" };

  const decimalToFraction = (value) => {
    let whole = Math.floor(value);
    const fraction = value - whole;
    let quarters = Math.round(fraction * 4);

    if (quarters === 4) {
      whole += 1;
      quarters = 0;
    }

    return `${whole}${FRACTION_GLYPHS[quarters]}`;
  };

  const decimalToCm = (value) => `${Math.round(value * 2.54)}`;

  const formatCell = (el, unit) => {
    const min = parseFloat(el.dataset.in);
    if (Number.isNaN(min)) return;

    const max = el.dataset.inMax ? parseFloat(el.dataset.inMax) : null;
    const format = unit === "cm" ? decimalToCm : decimalToFraction;

    el.textContent =
      max && max !== min ? `${format(min)} – ${format(max)}` : format(min);
  };

  const SCROLL_LOCK_MS = 600;

  const setActiveTab = (sizeGuide, tabKey) => {
    sizeGuide.querySelectorAll(".size-guide__tab").forEach((tab) => {
      const isActive = tab.dataset.tab === tabKey;
      tab.classList.toggle("active", isActive);
      tab.setAttribute("aria-current", isActive ? "true" : "false");
    });
  };

  // Tabs act as a scroll-to-section nav (not a show/hide switcher): clicking
  // scrolls the matching panel into view, and a scrollspy keeps the active
  // tab in sync as the merchant scrolls the drawer manually.
  const initTabs = (sizeGuide) => {
    const tabs = sizeGuide.querySelectorAll(".size-guide__tab");
    const panels = sizeGuide.querySelectorAll(".size-guide__panel");
    if (!panels.length) return;

    const tabsNav = sizeGuide.querySelector(".size-guide__tabs");
    let scrollLockUntil = 0;

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const panel = sizeGuide.querySelector(
          `.size-guide__panel[data-tab="${tab.dataset.tab}"]`
        );
        if (!panel) return;

        // The tabs bar is sticky and wraps to two rows on narrow screens, so
        // measure it on click (the drawer is open by then — at init it is
        // still display:none and reports 0) and feed it to scroll-margin-top.
        if (tabsNav) {
          sizeGuide.style.setProperty(
            "--size-guide-tabs-offset",
            `${tabsNav.offsetHeight + 12}px`
          );
        }

        scrollLockUntil = Date.now() + SCROLL_LOCK_MS;
        setActiveTab(sizeGuide, tab.dataset.tab);
        panel.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });


    const scrollRoot = sizeGuide.closest(".product-popup-modal__content-wrapper");

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < scrollLockUntil) return;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveTab(sizeGuide, entry.target.dataset.tab);
          }
        });
      },
      {
        root: scrollRoot || null,
        rootMargin: "-62px 0px -70% 0px",
        threshold: 0,
      }
    );

    panels.forEach((panel) => observer.observe(panel));
  };

  const initUnitToggle = (sizeGuide) => {
    const checkbox = sizeGuide.querySelector(".size-guide__unit-checkbox");
    if (!checkbox) return;

    const cells = sizeGuide.querySelectorAll("[data-in]");

    checkbox.addEventListener("change", () => {
      const unit = checkbox.checked ? "cm" : "in";
      cells.forEach((cell) => formatCell(cell, unit));
    });
  };

  const FIT_IMAGE_WIDTHS = [300, 450, 600, 900];

  const withWidth = (src, width) =>
    `${src}${src.includes("?") ? "&" : "?"}width=${width}`;

  // Keeps the Size & Fit panel in sync with the selected variant: the fit
  // model image is the variant's featured image, and measurements/description
  // come from variant metafields serialized into the panel's JSON config.
  const initVariantSync = (sizeGuide) => {
    const configEl = sizeGuide.querySelector("[data-size-guide-fit-data]");
    if (!configEl) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      return;
    }
    if (!config || !config.variants) return;

    if (
      typeof subscribe !== "function" ||
      typeof PUB_SUB_EVENTS === "undefined" ||
      !PUB_SUB_EVENTS.variantChange
    ) {
      return;
    }

    const setText = (selector, value) => {
      const el = sizeGuide.querySelector(selector);
      if (!el) return;
      el.textContent = value || "";
      el.hidden = !value;
    };

    const setImage = (variant) => {
      const wrapper = sizeGuide.querySelector(".size-guide__fit-model");
      if (!wrapper) return;

      const featuredImage = variant.featured_image;
      if (!featuredImage || !featuredImage.src) {
        wrapper.hidden = true;
        return;
      }

      let img = wrapper.querySelector(".size-guide__fit-model-image");
      if (!img) {
        img = document.createElement("img");
        img.className = "size-guide__fit-model-image";
        img.loading = "lazy";
        img.sizes = "(min-width: 750px) 240px, 40vw";
        wrapper.appendChild(img);
      }

      img.src = withWidth(featuredImage.src, 600);
      img.srcset = FIT_IMAGE_WIDTHS.filter(
        (width) => !featuredImage.width || width <= featuredImage.width
      )
        .map((width) => `${withWidth(featuredImage.src, width)} ${width}w`)
        .join(", ");
      img.alt = featuredImage.alt || "";
      if (featuredImage.width) img.width = featuredImage.width;
      if (featuredImage.height) img.height = featuredImage.height;
      wrapper.hidden = false;
    };

    const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (!sizeGuide.isConnected) {
        unsubscribe();
        return;
      }

      const variant = event && event.data && event.data.variant;
      if (!variant || event.data.sectionId !== sizeGuide.dataset.sectionId) {
        return;
      }

      const entry = config.variants[String(variant.id)] || {};
      setText(".size-guide__fit-measurements", entry.measurements);
      setText(".size-guide__fit-description", entry.description);
      setImage(variant);
    });
  };

  const initSizeGuide = (sizeGuide) => {
    if (!sizeGuide || sizeGuide.dataset.initialized) return;
    sizeGuide.dataset.initialized = "true";

    initTabs(sizeGuide);
    initUnitToggle(sizeGuide);
    initVariantSync(sizeGuide);
  };

  const init = (root) => {
    if (!root) return;
    root.querySelectorAll(".size-guide").forEach(initSizeGuide);
  };

  init(document.currentScript ? document.currentScript.parentElement : null);

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });
})();
